/**
 * =============================================================================
 * UNICORN SHIPPING INSURANCE
 * =============================================================================
 *
 * Auto-adds shipping insurance when cart has items. Respects user opt-out when
 * they uncheck; resets when cart is emptied.
 *
 * Variant ID: Theme Settings > Cart > Add product insurance
 * DO NOT add this product via cart/add.js - use the Unicorn checkbox.
 *
 * =============================================================================
 */

(function () {
  'use strict';

  const VARIANT_ID = window.MULBERRY_SHIPPING_INSURANCE?.variantId || '49106313543959';
  const UNICORN_PROP = '_unicorn_block';
  const TICK_UPSELL = 'tick_upsell';

  const DELAY_CLICK_MS = 300;
  const DELAY_PAGE_LOAD_MS = 500;
  const DELAY_FETCH_SYNC_MS = 300;
  const DELAY_DRAWER_MS = 200;
  const POLL_INTERVAL_MS = 250;
  const MAX_WAIT_MS = 6000;
  const PROGRAMMATIC_CLICK_GRACE_MS = 800;

  let prevItemCount = 0;
  let prevHasInsurance = false;
  let userOptedOut = false;
  let programmaticClickUntil = 0;
  let debounceTimer = null;

  const CHECKBOX_SELECTORS = [
    '.unicorn_tick_upsell input[type="checkbox"]',
    '.unicorn_checkbox',
    '.unicorn-tick-upsell input[type="checkbox"]',
    '.cart__shipping_insurance .myinsurance',
    '.myinsurance',
    '[data-unicorn-block="tick_upsell"] input[type="checkbox"]',
    '[data-unicorn-tick-upsell] input[type="checkbox"]',
    'cart-drawer-component input[type="checkbox"]',
    'cart-items-component input[type="checkbox"]',
  ];

  function getCartUrl() {
    return (window.Shopify?.routes?.root || '') + 'cart.js';
  }

  function hasShippingInsuranceInCart() {
    const data = window.unicornCartData;
    if (!data?.items || !Array.isArray(data.items)) return false;
    const vid = String(VARIANT_ID);
    return data.items.some(function (item) {
      const p = item.properties || {};
      return p[UNICORN_PROP] === TICK_UPSELL || String(item.variant_id) === vid;
    });
  }

  function cartHasItems() {
    const data = window.unicornCartData;
    if (!data) return false;
    const n = data.item_count ?? data.items?.length ?? 0;
    return n > 0;
  }

  function findInsuranceCheckbox() {
    for (let i = 0; i < CHECKBOX_SELECTORS.length; i++) {
      const el = document.querySelector(CHECKBOX_SELECTORS[i]);
      if (el && !el.checked) return el;
    }
    return null;
  }

  function waitForCheckboxAndClick() {
    if (!cartHasItems() || hasShippingInsuranceInCart()) return;

    const start = Date.now();

    function attempt() {
      if (hasShippingInsuranceInCart()) return;
      if (Date.now() - start > MAX_WAIT_MS) return;

      const cb = findInsuranceCheckbox();
      if (cb) {
        programmaticClickUntil = Date.now() + PROGRAMMATIC_CLICK_GRACE_MS;
        cb.click();
        return;
      }
      setTimeout(attempt, POLL_INTERVAL_MS);
    }

    const container = document.querySelector('cart-drawer-component') || document.querySelector('cart-items-component');
    if (container) {
      const obs = new MutationObserver(attempt);
      obs.observe(container, { childList: true, subtree: true });
      attempt();
      setTimeout(function () { obs.disconnect(); }, MAX_WAIT_MS);
    } else {
      attempt();
    }
  }

  function fetchCart() {
    const url = getCartUrl() + '?_=' + Date.now();
    return fetch(url, { cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        window.unicornCartData = window.unicornCartData || {};
        window.unicornCartData.items = data.items || [];
        window.unicornCartData.item_count = data.item_count ?? data.items?.length ?? 0;
        return window.unicornCartData;
      })
      .catch(function () { return null; });
  }

  function processCartUpdate(data) {
    if (!data) return;

    const newCount = data.item_count || 0;
    const hasInsurance = hasShippingInsuranceInCart();

    if (newCount === 0) userOptedOut = false;
    if (prevHasInsurance && !hasInsurance && Date.now() > programmaticClickUntil) {
      userOptedOut = true;
    }

    const shouldAdd = newCount >= 1 && !hasInsurance && !userOptedOut;
    if (shouldAdd) {
      setTimeout(waitForCheckboxAndClick, DELAY_CLICK_MS);
    }

    prevItemCount = newCount;
    prevHasInsurance = hasInsurance;
  }

  function onCartUpdate() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      debounceTimer = null;
      fetchCart().then(processCartUpdate);
    }, 100);
  }

  function onInsuranceUncheck(e) {
    if (Date.now() < programmaticClickUntil) return;
    const el = e.target;
    if (!el || !el.matches) return;
    const isInsurance = el.matches('.myinsurance') || el.closest('.cart__shipping_insurance') ||
      el.matches('.unicorn_checkbox') || el.closest('.unicorn_tick_upsell');
    if (isInsurance && !el.checked) userOptedOut = true;
  }

  function init() {
    fetchCart().then(function (data) {
      if (!data) return;
      prevItemCount = data.item_count ?? 0;
      prevHasInsurance = hasShippingInsuranceInCart();
      if (prevItemCount >= 1 && !prevHasInsurance && !userOptedOut) {
        setTimeout(waitForCheckboxAndClick, DELAY_PAGE_LOAD_MS);
      }
    });
  }

  function attachDrawerListener() {
    var drawer = document.querySelector('cart-drawer-component');
    var dialog = drawer ? drawer.querySelector('dialog') : null;
    if (!dialog || dialog.dataset.insuranceAttached) return;
    dialog.dataset.insuranceAttached = '1';
    dialog.addEventListener('toggle', function () {
      if (dialog.open) setTimeout(onCartUpdate, DELAY_DRAWER_MS);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
    document.addEventListener('DOMContentLoaded', attachDrawerListener);
  } else {
    init();
    attachDrawerListener();
  }
  setTimeout(attachDrawerListener, 1000);

  document.addEventListener('cart:update', onCartUpdate);
  document.addEventListener('change', onInsuranceUncheck);
  document.addEventListener('globo.preorder.update.cart', function () {
    setTimeout(onCartUpdate, 100);
  });

  var _fetch = window.fetch;
  if (typeof _fetch === 'function') {
    window.fetch = function () {
      var res = _fetch.apply(this, arguments);
      var url = (typeof arguments[0] === 'string' ? arguments[0] : (arguments[0] && arguments[0].url)) || '';
      if (url.indexOf('/cart/change') >= 0 || url.indexOf('/cart/update') >= 0) {
        setTimeout(onCartUpdate, DELAY_FETCH_SYNC_MS);
      }
      return res;
    };
  }
})();
